import {createAsyncThunk} from '@reduxjs/toolkit';
import {AppThunkApiConfig} from '../../createStore';
import {ActivityType, DeskAmenity, mapiqApi} from '@lib/infrastructure';
import {AreaResource, FacilityResource, Resource, RoomResource} from './types';
import {getCalendarDomainPreference} from '../meetings/helpers';
import {RoomActivity, RoomEquipment} from '../entities';

export const loadBuildingResources = createAsyncThunk<
  {buildingId: string; resources: Resource[]; floordIds: string[]},
  string,
  AppThunkApiConfig
>('buildingResources/loadBuilding', async (buildingId, {extra, getState}) => {
  const client = extra.apiClient;
  const t = extra.i18n.lookup;
  const api = mapiqApi(client);

  const calendarDomain = getCalendarDomainPreference(getState());

  const buildingsResponse = await api.getBuildingResource(buildingId, calendarDomain);

  for (const floor of buildingsResponse.floors) {
    floor.areas = floor.areas.filter((area) => {
      return area.capacity > 0;
    });
  }

  return {
    buildingId,
    floordIds: buildingsResponse.floors.map(({id}) => id),
    resources: buildingsResponse.floors.flatMap((f) => {
      const rooms: RoomResource[] = f.meetingRooms.map((room) => ({
        id: room.id,
        buildingId,
        floorId: f.id,
        name: room.displayName,
        floorName: f.displayName,
        buildingName: buildingsResponse.displayName,
        type: 'room',
        data: {
          roomPictureHash: room.roomPictureHash,
          activities: room.activities as RoomActivity[],
          equipment: room.equipment as RoomEquipment[],
          capacity: room.capacity,
          email: room.email,
        },
        searchTags: [t('hereAndNow:ResourceFilterRooms')],
      }));

      const areas: AreaResource[] = f.areas.map((area) => ({
        id: area.id,
        buildingId,
        floorId: f.id,
        name: area.displayName,
        floorName: f.displayName,
        buildingName: buildingsResponse.displayName,
        type: 'area',
        data: {
          workspaceBookingPolicyType: area.workspaceBookingPolicyType,
          capacity: area.capacity,
          activityTypes: area.activityTypes as ActivityType[],
          deskAmenities: area.deskAmenities as DeskAmenity[],
        },
        searchTags: [t('hereAndNow:ResourceFilterAreas')],
      }));

      const facilities: FacilityResource[] = f.facilities.map((facility) => ({
        id: facility.id,
        buildingId,
        floorId: f.id,
        name: facility.displayName ?? t(`facilities:facilityType_${facility.facilityType.id}`),
        floorName: f.displayName,
        buildingName: buildingsResponse.displayName,
        type: 'facility',
        data: {
          facilityType: {...facility.facilityType},
        },
        searchTags: [t(`facilities:facilityType_${facility.facilityType.id}`)],
      }));

      return Array<Resource>()
        .concat(rooms)
        .concat(areas)
        .concat(facilities)
        .sort((r1, r2) => r1.name.localeCompare(r2.name));
    }),
  };
});
